import React from 'react';

import { BellOutlined, CheckCircleOutlined, RocketOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { ComingSoonFeatureConfig, ComingSoonFeatureInterest } from 'types/comingSoonFeatures';

import ComingSoonPreviewCards from './ComingSoonPreviewCards';
import { Source } from './ComingSoonStyles';

const CTABox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  margin-top: 40px;
  padding: 40px;
  border: 1px solid var(--white-60);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
`;

const CTAContent = styled.div`
  flex: 1 1 320px;
  max-width: 720px;
`;

const CTATitle = styled.div`
  font-size: 20px;
`;

const CTAButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin: 15px 0 10px;
  padding: 15px;
  border: none;
  border-radius: 10px;
  background-image: linear-gradient(
    90deg,
    var(--flamingo-pink),
    var(--aici-action-icon-gradient-mid) 60%,
    var(--competitor-purple)
  );
  color: var(--white);
  font-family: var(--font-bold);
  font-size: 16px;
  cursor: pointer;
`;

const Confirmation = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 12px 0 10px;
  padding: 20px;
  border-left: 5px solid var(--positive-green);
  border-radius: 10px;
  background: var(--white);
  color: var(--text-primary);

  .anticon {
    font-size: 24px;
    color: var(--positive-green);
  }
`;

const ConfirmationTitle = styled.div`
  font-family: var(--font-bold);
  color: var(--positive-green);
`;

type Props = {
  feature: ComingSoonFeatureConfig;
  interest?: ComingSoonFeatureInterest;
  pilotCommunityNames: string;
  onApply: () => void;
};

const ComingSoonCallToAction = ({ feature, interest, pilotCommunityNames, onApply }: Props) => (
  <CTABox data-test-id={`coming-soon-cta-${feature.key}`}>
    <ComingSoonPreviewCards preview={feature.preview} />
    <CTAContent>
      <CTATitle>
        {feature.isPilot ? `Get early access to ${feature.label}` : 'This feature will soon be in early pilot'}
      </CTATitle>
      <Source>
        {feature.isPilot
          ? 'This feature is in early pilot. If you’d like to apply to join our pilot, join the list and we’ll reach out with more information.'
          : `Interested in ${feature.label}? Join the list and we’ll reach out with more information when the pilot opens.`}
      </Source>
      {interest ? (
        <Confirmation data-test-id={`coming-soon-confirmation-${feature.key}`}>
          <CheckCircleOutlined />
          <div>
            <ConfirmationTitle>
              {feature.isPilot ? `You’re in - ${pilotCommunityNames}.` : 'You’re on the list!'}
            </ConfirmationTitle>
            {feature.isPilot
              ? 'We’ll reach out shortly to kick off your pilot.'
              : 'We’ll let you know the moment it’s ready.'}
          </div>
        </Confirmation>
      ) : (
        <CTAButton type="button" data-test-id={`coming-soon-cta-button-${feature.key}`} onClick={onApply}>
          {feature.isPilot ? <RocketOutlined /> : <BellOutlined />}
          {feature.isPilot ? 'Apply to the Pilot' : 'Notify Me When Ready'}
        </CTAButton>
      )}
      <Source>Note: For paid features, the fee will be waived during the pilot period.</Source>
    </CTAContent>
  </CTABox>
);

export default ComingSoonCallToAction;
